// @flow

import * as React from 'react';
import ReactDOM from 'react-dom';
import keycode from 'keycode';
import { fromPairs, isFunction, isUndefined } from 'lodash';

type Handler = (event?: KeyboardEvent) => void;

type HandlerConfig = {
  handler: Handler,
  stopPropagation?: boolean,
  preventDefault?: boolean,
};

type Handlers = {
  [string]: Handler | HandlerConfig,
};

type HandlersByKeycode = {
  [string]: HandlerConfig,
};

export type Props = {
  children: React.Node,
  isGlobalEvent?: boolean,
  handlers: Handlers,
};

type State = {
  handlers: HandlersByKeycode,
};

const createHandlers = (
  keyNamesToHandlers: Handlers = {}
): HandlersByKeycode => {
  return fromPairs(
    Object.keys(keyNamesToHandlers).map(keyName => {
      const keyCode = keycode(keyName);
      if (isUndefined(keyCode)) {
        throw new Error(`Unknown keycode for ${keyName}.`);
      }
      let handler = keyNamesToHandlers[keyName];
      if (isFunction(handler)) {
        handler = {
          handler,
        };
      }
      return [`${keyCode}`, handler];
    })
  );
};

class Keydown extends React.Component<Props, State> {
  static defaultProps = {
    isGlobalEvent: false,
  };

  state = {
    handlers: createHandlers(this.props.handlers),
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.props.handlers !== nextProps.handlers) {
      this.setState({
        handlers: createHandlers(this.props.handlers),
      });
    }
  }

  componentDidMount() {
    const target = this.getEventTarget();
    if (target !== null) {
      target.addEventListener('keydown', this.onKeyDown);
    }
  }

  componentWillUnmount() {
    const target = this.getEventTarget();
    if (target !== null) {
      target.removeEventListener('keydown', this.onKeyDown);
    }
  }

  getEventTarget = () => {
    const ownNode = ReactDOM.findDOMNode(this);
    return this.props.isGlobalEvent ? document : ownNode;
  };

  isTargetWithinOwnNode = (targetNode: ?Node) => {
    const ownNode: ?Element | ?Text = ReactDOM.findDOMNode(this);
    return !!ownNode && ownNode.contains(targetNode);
  };

  isApplicableTarget = (targetNode: ?Node) => {
    return this.props.isGlobalEvent || this.isTargetWithinOwnNode(targetNode);
  };

  onKeyDown = (event: KeyboardEvent) => {
    const handlerConfig = this.state.handlers[`${event.keyCode}`];
    const target = event.target instanceof Node ? event.target : null;

    if (handlerConfig) {
      const handler = handlerConfig.handler;
      if (isFunction(handler) && this.isApplicableTarget(target)) {
        handler(event);
      }
      if (handlerConfig.stopPropagation) {
        event.stopPropagation();
      }
      if (handlerConfig.preventDefault) {
        event.preventDefault();
      }
    }
  };

  render() {
    return !this.props.children
      ? null
      : React.Children.only(this.props.children);
  }
}

export default Keydown;
